'use client'

import { useEffect, useRef } from 'react'
import { Box, useColorModeValue, useToken } from '@chakra-ui/react'
import Map from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import { fromLonLat } from 'ol/proj'
import { Icon, Style } from 'ol/style'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import 'ol/ol.css'
import XYZ from 'ol/source/XYZ'
import MapShareButton from './components/MapShareButton'
import { generateDefaultMapPinSVG } from 'components/map'

type StaticMapProps = {
  lat: number
  long: number
  afacode?: string
  shareButton?: boolean
  height?: string
}

export default function StaticMap({ lat, long, afacode, shareButton = true, height = '200px' }: StaticMapProps) {
  const mapRef = useRef<HTMLDivElement>(null)

  const [primary300, primary400, primary800] = useToken('colors', ['primary.300', 'primary.400', 'primary.800'])

  const fillLight = useColorModeValue(primary400, primary300)
  const strokeDark = useColorModeValue(primary800, primary800)

  const markerIconUrl = generateDefaultMapPinSVG(fillLight, strokeDark)

  useEffect(() => {
    if (!mapRef.current) return

    const coords = fromLonLat([long, lat])

    const marker = new Feature({
      geometry: new Point(coords),
    })

    marker.setStyle(
      new Style({
        image: new Icon({
          anchor: [0.5, 1],
          src: markerIconUrl, // Coloque sua imagem de marcador pública aqui
          scale: 0.1,
        }),
      })
    )

    const vectorLayer = new VectorLayer({
      source: new VectorSource({
        features: [marker],
      }),
    })

    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new XYZ({
            url: 'https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
            attributions:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
          }),
        }),
        vectorLayer,
      ],
      view: new View({
        center: coords,
        zoom: 16,
      }),
      controls: [],
      interactions: [],
    })

    return () => map.setTarget(undefined)
  }, [lat, long])

  return (
    <Box ref={mapRef} w="100%" h={height} position="relative" borderRadius="lg" overflow="hidden">
      {shareButton && <MapShareButton lat={lat} long={long} afacode={afacode} />}
    </Box>
  )
}
